/**
 * User-facing error message helpers for API failures.
 *
 * The goal is to keep technical noise (HTTP bodies, ASP.NET dev pages,
 * stack traces) out of the UI while still preserving the full detail in
 * the developer console for debugging.
 */

import { ApiError, AuthRedirectError, type ProblemDetails } from './apiService';
import { MarketCategoriesError } from './marketCategoriesService';
import { BenefitsPerTierError } from './benefitsPerTierService';

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Extracts a short, displayable message from a parsed JSON error body.
 * Tries (in order): RFC 7807 `title`/`detail`, our controllers'
 * `{ error }` shape, and a generic `{ message }`. Returns undefined when
 * nothing usable is present, so the caller can fall back to a per-status
 * canned message.
 */
function extractBackendMessage(details: ProblemDetails | undefined): string | undefined {
  if (!details) return undefined;
  const candidates = [details.title, details.detail, details.error, details.message];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
}

/**
 * Returns a short, human-readable message suitable for display in a
 * MessageBar or inline error label. NEVER returns the raw response body
 * or generic Error.message (those leak technical detail and can be huge).
 */
export function getFriendlyApiErrorMessage(err: unknown, fallback?: string): string {
  // Client-side validation errors are already user-friendly by design.
  if (err instanceof MarketCategoriesError) {
    return err.message;
  }
  if (err instanceof BenefitsPerTierError) {
    return err.message;
  }

  if (err instanceof AuthRedirectError) {
    return 'Redirecting to sign in…';
  }

  if (err instanceof ApiError) {
    const backendMessage = extractBackendMessage(err.problemDetails);

    switch (err.status) {
      case 400:
        return (
          backendMessage ||
          'The request was invalid. Please review the form and try again.'
        );
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return "You don't have permission to perform this action, or the server can't reach the database. Contact your administrator.";
      case 404:
        return (
          backendMessage ||
          "The requested item couldn't be found. It may have been deleted or renamed."
        );
      case 409:
        return (
          backendMessage ||
          'The data was changed by someone else. Refresh and try again.'
        );
      default:
        if (err.status >= 500) {
          return 'Something went wrong on the server. Please try again in a moment.';
        }
        return backendMessage || `Unexpected error (status ${err.status}).`;
    }
  }

  // Network failures (fetch rejects with TypeError when the request never
  // reaches the server, e.g. DNS error, offline, CORS preflight failure).
  if (err instanceof TypeError) {
    return "Couldn't reach the server. Check your connection and try again.";
  }

  return fallback ?? DEFAULT_FALLBACK;
}

/**
 * Logs the full technical detail of an error to the developer console
 * without leaking it into the UI. Call this alongside
 * `getFriendlyApiErrorMessage` so the user sees a friendly message and
 * developers see everything they need to debug.
 */
export function logApiError(context: string, err: unknown): void {
  if (err instanceof ApiError) {
    console.error(`[${context}]`, err.message, {
      status: err.status,
      statusText: err.statusText,
      method: err.method,
      url: err.url,
      problemDetails: err.problemDetails,
      body: err.body,
    });
    return;
  }
  console.error(`[${context}]`, err);
}
